import React from 'react';

interface PropertyNumberProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  step?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  suffix?: string;
}

export const PropertyNumber: React.FC<PropertyNumberProps> = ({
  label,
  value,
  onChange,
  step = 1,
  min,
  max,
  disabled = false,
  suffix = ''
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = parseFloat(e.target.value);
    
    if (isNaN(newValue)) {
      newValue = 0;
    }
    
    if (min !== undefined && newValue < min) {
      newValue = min;
    }
    if (max !== undefined && newValue > max) {
      newValue = max;
    }
    
    onChange(newValue);
  };

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    // Prevent page scroll and stop propagation to parent container
    e.preventDefault();
    e.stopPropagation();
    
    // Get scroll direction (negative = scroll up = increase, positive = scroll down = decrease)
    const delta = -Math.sign(e.deltaY);
    
    // Multiply step by 10 if Shift is held
    const effectiveStep = e.shiftKey ? step * 10 : step;
    
    let newValue = value + (delta * effectiveStep);
    
    // Apply min/max constraints
    if (min !== undefined && newValue < min) {
      newValue = min;
    }
    if (max !== undefined && newValue > max) {
      newValue = max;
    }
    
    onChange(newValue);
  };

  // Prevent container scroll when hovering over the input wrapper
  const handleWrapperWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    // Completely block scroll propagation
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className="flex flex-1 items-center gap-1.5" onWheel={handleWrapperWheel}>
      <label className="text-gray-600 dark:text-gray-400 text-sm font-medium w-4 flex-shrink-0">
        {label}
      </label>
      <input
        type="number"
        value={Math.round(value * 100) / 100}
        onChange={handleChange}
        onWheel={handleWheel}
        step={step}
        min={min}
        max={max}
        disabled={disabled}
        className="w-full bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      {suffix && (
        <span className="text-gray-600 dark:text-gray-400 text-xs w-4 flex-shrink-0">{suffix}</span>
      )}
    </div>
  );
};

